/**
 * Canonical search-URL builders — single source for the scope-based scheme so
 * every entry point (home, banners, pickers, sitemap, SearchBar) emits the same
 * `/{locale}/s/{romanized-slug}?scope=...` shape. Keeps internal navigation off
 * the legacy-URL 301 path (see page.tsx redirect + resolveLegacyRegion).
 */

import {
  citySlug,
  countrySlug,
  cityByAnyName,
  countryByCode,
  KEYWORD_SLUG,
  NEARBY_SLUG,
} from "./place-slug";
import { getCityBounds, type Bounds, type City } from "@/config/locations";

type Extra = Record<string, string | number | undefined>;

function build(
  locale: string,
  slug: string,
  params: Array<[string, string | number | undefined]>,
): string {
  const usp = new URLSearchParams();
  for (const [k, v] of params) {
    if (v !== undefined && v !== null && `${v}` !== "") usp.set(k, `${v}`);
  }
  const qs = usp.toString();
  return `/${locale}/s/${slug}${qs ? `?${qs}` : ""}`;
}

/**
 * Bounds → the exact `[key, value]` strings this module puts in a URL.
 * Exported so the indexing check in `/s/[region]` can compare a request's raw
 * params against the city's default bounds *as strings* — recomputing floats and
 * comparing numerically would drift, this is deterministic by construction.
 */
export function boundsPairs(b?: Bounds | null): Array<[string, string | undefined]> {
  if (!b) return [];
  return [
    ["ne_lat", `${b.ne_lat}`],
    ["ne_lng", `${b.ne_lng}`],
    ["sw_lat", `${b.sw_lat}`],
    ["sw_lng", `${b.sw_lng}`],
  ];
}

/** City scope from a City object. Uses the city's radius_km bounds unless overridden. */
export function cityScopeHref(
  locale: string,
  city: City,
  opts?: { bounds?: Bounds | null; q?: string; categories?: string; timestamp?: boolean },
): string {
  const bounds = opts?.bounds ?? getCityBounds(city.name.en, "en");
  return build(locale, citySlug(city), [
    ["scope", "city"],
    ["country", city.countryCode],
    ...boundsPairs(bounds),
    ["categories", opts?.categories],
    ["q", opts?.q],
    ["t", opts?.timestamp && bounds ? "1" : undefined],
  ]);
}

/** City scope from any localized name; falls back to keyword scope if unknown. */
export function cityScopeHrefByName(
  locale: string,
  cityName: string,
  opts?: { bounds?: Bounds | null; q?: string; categories?: string; timestamp?: boolean },
): string {
  const city = cityByAnyName(cityName);
  if (!city) return keywordScopeHref(locale, { q: opts?.q, categories: opts?.categories });
  return cityScopeHref(locale, city, opts);
}

/** Country-wide scope. slug = romanized country name, ISO in `country`. */
export function countryScopeHref(
  locale: string,
  countryCode: string,
  opts?: { q?: string; categories?: string },
): string {
  const country = countryByCode(countryCode);
  const slug = country ? countrySlug(country) : KEYWORD_SLUG;
  return build(locale, slug, [
    ["scope", "country"],
    ["country", countryCode],
    ["categories", opts?.categories],
    ["q", opts?.q],
  ]);
}

/** Pure keyword / category browse (no geo). */
export function keywordScopeHref(
  locale: string,
  opts?: { q?: string; categories?: string; extra?: Extra },
): string {
  const extra: Array<[string, string | number | undefined]> = opts?.extra
    ? Object.entries(opts.extra)
    : [];
  return build(locale, KEYWORD_SLUG, [
    ["scope", "keyword"],
    ["q", opts?.q],
    ["categories", opts?.categories],
    ...extra,
  ]);
}

/** Nearby (IP/GPS). Optional bounds for map fit. */
export function nearbyScopeHref(
  locale: string,
  opts?: { bounds?: Bounds | null; timestamp?: boolean },
): string {
  return build(locale, NEARBY_SLUG, [
    ["scope", "nearby"],
    ...boundsPairs(opts?.bounds),
    ["t", opts?.timestamp && opts?.bounds ? "1" : undefined],
  ]);
}

/**
 * Partner scope — slug is the partner_info slug; loader resolves it to partner_id.
 * `categories` narrows the partner's products client-side (same filter as every scope),
 * so a product detail can open "this partner's branches for this kind of product".
 */
export function partnerScopeHref(
  locale: string,
  partnerSlug: string,
  opts?: { categories?: string; pin?: string },
): string {
  return build(locale, partnerSlug, [
    ["scope", "partner"],
    ["categories", opts?.categories],
    // 목록 맨 앞에 둘 상품 id (SearchPageClient pinnedProductId)
    ["pin", opts?.pin],
  ]);
}
